import { Backdrop, Box, Divider, Grid, Typography, useTheme } from '@mui/material';
import { alpha, Stack } from '@mui/system';
import { DefinedUseQueryResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { concat } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import MainContainer from 'src/components/common/MainContainer';
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import CompanyInformation from 'src/features/CompanyDetail/Common/CompanyInformation';
import NegativeMedia from 'src/features/CompanyDetail/NegativeMedia';
import GeneralInformation from 'src/features/CompanyDetail/OverView/GeneralInformation';
import IndustryClassifications from 'src/features/CompanyDetail/OverView/IndustryClassifications';
import SocialReview from 'src/features/CompanyDetail/Reviews/SocialReview';
import ContactTable from 'src/features/CompareCompany/ContactTable';
import { MENUS_LOGIN_PAGE } from 'src/features/Login/Common/config';
import { CompanySocialReviews } from 'src/model/company';
import { useQueryCompanyDetail, useQueryCompanySocialReview } from 'src/services/queries';
import { LIMIT_GET_LIST, MARKETING_PAGE } from 'src/utils/constants/config';
import { TypeCompanyProfileType } from 'src/utils/constants/enum';
import {
  ICON_DISCLAIMER_COMPANY_DATA,
  ICON_SUPPLEMENT_RECORDS_DATA,
  LOGO_HEADER_URL,
} from 'src/utils/constants/imagesUrl';
import { convertSvgToCanvas } from 'src/utils/helpers/utilities';

const MENUS_FOOTER = concat(
  [
    {
      text: 'ADAMftd.com',
      path: MARKETING_PAGE,
      target: '_blank',
    },
  ],
  MENUS_LOGIN_PAGE,
);

const CompanyPrintPage = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const {
    palette: {
      background: { paper1 },
      divider,
      warning,
    },
    spacing,
    shape: { borderRadius },
    zIndex,
  } = useTheme();
  const { id } = useParams();
  const [isExportLoading, setIsExportLoading] = useState(false);
  const { data, isFetching } = useQueryCompanyDetail(id ?? '', true);
  const socialReview: DefinedUseQueryResult<CompanySocialReviews, Error> = useQueryCompanySocialReview({ id: id });

  const isNonOfficial = (data.data?.official_tags || []).includes(TypeCompanyProfileType.NON_OFFICIAL);

  const header = useMemo(() => {
    return (
      <Stack direction={'row'} alignItems="center" justifyContent={'space-between'}>
        <Box height={58} component={'img'} src={LOGO_HEADER_URL} />
        <Typography variant="body2" color={'text.secondary'}>
          This file was exported on <strong>{dayjs().format('MMMM D, YYYY')}</strong> by ADAMftd.org
        </Typography>
      </Stack>
    );
  }, []);

  const renderUnofficialRecordDescription = () => {
    if (!isNonOfficial) return null;
    return (
      <Grid item xs={12}>
        <Box
          display={'flex'}
          gap={spacing(4)}
          alignItems="center"
          px={spacing(3)}
          borderRadius={borderRadius / 2}
          py={spacing(2)}
          sx={{ border: '1px', borderStyle: 'solid', borderColor: divider }}
          bgcolor={alpha(warning.light, 0.5)}
        >
          <Box src={ICON_SUPPLEMENT_RECORDS_DATA} component={'img'} />
          <Box>
            <Typography variant="h5">What is an Unofficial Record?</Typography>
            <Typography variant="body1">
              An unofficial record refers to a company entry in our database that has not yet been matched to an
              official government registry or LEI-synchronized company name. This can happen for several reasons: the
              jurisdiction of the company may not provide public access to official registry records, our system may
              still be in the process of synchronizing with the relevant government database, or the company name in
              Customs or Bill-of Lading data may differ significantly from the officially registered name, making
              reconciliation impossible. While official records represent verified information directly from government
              sources, unofficial records often exist as placeholders until further verification is completed or
              official data becomes accessible.
            </Typography>
          </Box>
        </Box>
      </Grid>
    );
  };

  const convertAllImagesBase64ToCanvas = async () => {
    if (!pageRef.current) return;
    const imagesBase64 = pageRef.current.querySelectorAll<HTMLImageElement>('img[src^="data:image/svg"]');
    const conversions = Array.from(imagesBase64).map((imageBase64) => convertSvgToCanvas(imageBase64));
    await Promise.all(conversions);
  };

  const handleExportPDF = async () => {
    setIsExportLoading(true);
    if (!pageRef.current) return;
    await convertAllImagesBase64ToCanvas();
    const canvas = await html2canvas(pageRef.current, {
      useCORS: true,
      scale: 1,
      allowTaint: true,
      imageTimeout: 0,
    });
    const imgData = canvas.toDataURL('image/png');
    const doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
    });
    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = doc.internal.pageSize.getHeight();
    doc.addImage(imgData, 'png', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
    doc.save('document.pdf');
    setIsExportLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: paper1,
      }}
    >
      <Backdrop sx={(theme) => ({ color: '#fff', zIndex: zIndex.drawer + 1 })} open={isFetching} />
      <ButtonWithLoading
        isLoading={isExportLoading}
        id="company-print-button-export-pdf"
        onClick={handleExportPDF}
        isRounded
        variant="contained"
        sx={{ position: 'fixed', top: '50%' }}
      >
        Export PDF
      </ButtonWithLoading>
      <MainContainer ref={pageRef} sx={{ marginTop: 0, background: paper1 }}>
        <Grid spacing={3} container>
          <Grid item xs={12}>
            {header}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Box my={spacing(-3)}>
              <CompanyInformation isHideActions company={data?.data} isFetching={isFetching} />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <WhiteBoxContent height={'100%'}>
              <GeneralInformation dataCompany={data?.data} />
            </WhiteBoxContent>
          </Grid>

          <Grid item xs={6}>
            <WhiteBoxContent height={'100%'}>
              <IndustryClassifications dataCompany={data?.data} />
            </WhiteBoxContent>
          </Grid>

          <Grid item xs={12}>
            <WhiteBoxContent>
              <ContactTable id={id} />
            </WhiteBoxContent>
          </Grid>

          <Grid item xs={12}>
            <NegativeMedia
              tableProps={{
                maxRow: LIMIT_GET_LIST,
              }}
              company={data.data}
              isPagination={false}
            />
          </Grid>

          <Grid item xs={12}>
            <SocialReview socialReview={socialReview} id={id} />
          </Grid>

          <Grid item xs={12}>
            <Box
              display={'flex'}
              gap={spacing(4)}
              alignItems="center"
              px={spacing(3)}
              borderRadius={borderRadius / 2}
              py={spacing(2)}
              sx={{ border: '1px', borderStyle: 'solid', borderColor: divider }}
              bgcolor={alpha(warning.light, 0.5)}
            >
              <Box src={ICON_DISCLAIMER_COMPANY_DATA} component={'img'} />
              <Box>
                <Typography variant="h5">Supplementing Records with Unverified Data</Typography>
                <Typography variant="body1">
                  We also supplement our records with additional information from third-party, unofficial sources such
                  as website addresses, social media pages, company descriptions, reviews, sentiment analysis, and media
                  coverage. While this data provides a broader perspective and may offer useful insights, it is
                  important to understand that it is not verified against official sources and should not be solely
                  relied upon for critical decisions. These unofficial sources are intended to complement official
                  records where available, but their accuracy and reliability can vary significantly.
                </Typography>
              </Box>
            </Box>
          </Grid>

          {renderUnofficialRecordDescription()}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid xs={12} item>
            <Stack direction={'row'} justifyContent="space-between" alignItems={'center'}>
              <Box height={58} component={'img'} src={LOGO_HEADER_URL} />
              <Stack direction="row" spacing={3}>
                {MENUS_FOOTER.map((item: { text: string; path: string; target?: string }) => (
                  <RouterLinkNoUnderline target={item.target || ''} key={item.path} to={item.path}>
                    <Typography variant="subtitle1" color="text.secondary">
                      {item.text}
                    </Typography>
                  </RouterLinkNoUnderline>
                ))}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </MainContainer>
    </Box>
  );
};

export default CompanyPrintPage;
